/**
 * Query options for each structure module, which load the module in its own
 * chunk through the query cache.
 *
 * The cache holds the whole module namespace with no expiry, so once a module
 * has loaded, `useModule` returns it without suspending, typed from the
 * module's own exports.
 */

import { useSuspenseQuery } from '@tanstack/react-query';
import type { StructureModule } from 'util/structure-module';

type ModuleNamespace = { structureModule: StructureModule };

export type StructureModuleQuery<TModule extends ModuleNamespace = ModuleNamespace> = ReturnType<
    typeof structureModuleQueryOptions<TModule>
>;

function structureModuleQueryOptions<TModule extends ModuleNamespace>({
    structureKey,
    moduleFn,
}: {
    structureKey: string;
    moduleFn: () => Promise<TModule>;
}) {
    return {
        queryKey: ['structureModule', structureKey],
        queryFn: moduleFn,
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
    } as const;
}

export function useModule<TModule extends ModuleNamespace>(moduleQuery: StructureModuleQuery<TModule>) {
    const { data } = useSuspenseQuery(moduleQuery);
    return data;
}

export const accountModuleQuery = structureModuleQueryOptions({
    structureKey: 'account',
    moduleFn: () => import('feature/account/account-module'),
});

export const adminModuleQuery = structureModuleQueryOptions({
    structureKey: 'admin',
    moduleFn: () => import('feature/admin/admin-module'),
});

export const articleModuleQuery = structureModuleQueryOptions({
    structureKey: 'article',
    moduleFn: () => import('feature/article/article-module'),
});

export const conversationHomeModuleQuery = structureModuleQueryOptions({
    structureKey: 'conversation-home',
    moduleFn: () => import('feature/conversation-home/conversation-home-module'),
});

export const developerToolsModuleQuery = structureModuleQueryOptions({
    structureKey: 'developer-tools',
    moduleFn: () => import('feature/developer-tools/developer-tools-module'),
});

export const editorialDashModuleQuery = structureModuleQueryOptions({
    structureKey: 'editorialdash',
    moduleFn: () => import('feature/editorial-dash/editorial-dash-module'),
});

export const loginModuleQuery = structureModuleQueryOptions({
    structureKey: 'login',
    moduleFn: () => import('feature/login/login-module'),
});

export const moderationDashModuleQuery = structureModuleQueryOptions({
    structureKey: 'moderationdash',
    moduleFn: () => import('feature/moderation-dash/moderation-dash-module'),
});

export const profileModuleQuery = structureModuleQueryOptions({
    structureKey: 'profile',
    moduleFn: () => import('feature/profile/profile-module'),
});

export const questionModuleQuery = structureModuleQueryOptions({
    structureKey: 'question',
    moduleFn: () => import('feature/question/question-module'),
});

export const rankingEvalModuleQuery = structureModuleQueryOptions({
    structureKey: 'ranking-eval',
    moduleFn: () => import('feature/ranking-eval/ranking-eval-module'),
});

export const settingsModuleQuery = structureModuleQueryOptions({
    structureKey: 'settings',
    moduleFn: () => import('feature/settings/settings-module'),
});

export const videoVotingModuleQuery = structureModuleQueryOptions({
    structureKey: 'videovoting',
    moduleFn: () => import('feature/videovoting/videovoting-module'),
});
